/**
 * Inicio del WhatsApp oficial (`/activa/oficial`): la puerta de entrada.
 *
 * De arriba abajo, en el orden en que el operador tiene que actuar: qué falta para poder enviar,
 * qué alertas hay, los cuatro números que importan (número, cupo de hoy, saldo, plantillas), cómo
 * vienen las plantillas y las últimas campañas.
 *
 * «Un dato que no se pudo medir se dice, no se disfraza de 0»: si el resumen no llega, la pantalla
 * dice «No pudimos traer el resumen» y ofrece reintentar. Nunca ceros. Se refresca cada 60 s.
 *
 * El cupo: `limite` null SÓLO con `ilimitado: true` (el backend ya manda 250 cuando Meta no dio
 * un dato legible). `textoCupo` lo lee así; acá no se interpreta nada por nuestra cuenta.
 *
 * Con el servidor en twilio, nada del simulador se muestra como real (logic/resumen.ts): sin
 * número no hay número ni cupo (aunque quede el sender simulado), las plantillas que no sirven con
 * la cuenta van aparte («1 simulada: no sirve con Twilio») y la campaña de prueba lleva su chip.
 *
 * Es el «Inicio» de WhatsApp (así se llama en el menú). Ya no lleva «Twilio este mes» (saldo en Twilio
 * y lo que Twilio facturó): es lo que Twilio le cobra a Deenex, no lo de la marca, y está en Estadísticas →
 * Detalle técnico y en Configuración → Conexión (técnico).
 *
 * «Conversaciones» viene en el mismo resumen (sin otro pedido): cuántas personas esperan respuesta con la
 * ventana de 24 h abierta (el número del badge y del filtro por defecto de la bandeja) y cuántas sin responder
 * hay en total. Las primeras son PARTE de las segundas, y se dicen en una sola frase: «X sin responder · Y con
 * la ventana abierta» se leía como dos grupos distintos.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, Circle, FileText, Gauge, Info, MessagesSquare, OctagonAlert, Plus, RefreshCw, Send, Smartphone, Wallet,
} from "lucide-react";
import type { AlertaResumen, EstadoPlantilla, PaginaRespuestas, RespuestaEntrante, Resumen } from "../types";
import type { ResumenConversaciones } from "../types.conversaciones";
import { R_CONVERSACIONES } from "../logic/conversaciones";
import { oficialApi } from "../api";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { useCuentaOficial } from "../contexto";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { alertasParaElComercio, alertasSinRepetir, destinoAlerta, destinoAlertaParaCliente, pasosPuestaEnMarcha, type PasoPuestaEnMarcha } from "../logic/alertas";
import { usd } from "../logic/formato";
import { ESPERA, textoNoUsables } from "../logic/textos";
import { tileCupo, tileNumero } from "../logic/resumen";
import { usePolling } from "../hooks/usePolling";
import { ChipEstadoCampanaOficial } from "../components/ChipEstadoCampanaOficial";
import { ListaRespuestas } from "../components/ListaRespuestas";
import { Tarjeta } from "../components/Tarjeta";
import StatTile from "../../campana-nueva/components/StatTile";
import { num, pct, plural } from "../../campana-nueva/utils/format";

const R = "/activa/oficial";
const REFRESCO_MS = 60_000;

const ORDEN_NIVEL: Record<string, number> = { corte: 0, alerta: 1, info: 2 };

/** Chips de plantillas por estado del Resumen: cada uno lleva a la lista ya filtrada. */
const CHIPS_PLANTILLAS: { label: string; filtro: string; estados: EstadoPlantilla[]; cls: string }[] = [
  { label: "Aprobadas", filtro: "aprobadas", estados: ["approved"], cls: "bg-emerald-50 text-emerald-700" },
  { label: "En revisión", filtro: "revision", estados: ["received", "pending"], cls: "bg-amber-50 text-amber-700" },
  { label: "Rechazadas", filtro: "rechazadas", estados: ["rejected"], cls: "bg-red-50 text-red-600" },
  { label: "Pausadas o deshabilitadas", filtro: "frenadas", estados: ["paused", "disabled"], cls: "bg-orange-50 text-orange-700" },
  { label: "Borradores", filtro: "borradores", estados: ["borrador", "unsubmitted"], cls: "bg-slate-100 text-slate-600" },
];

/** El paso dicho como acción para el equipo, que es quien lo hace: «tu número» era el del comercio. */
const TITULO_PASO_EQUIPO: Partial<Record<PasoPuestaEnMarcha["clave"], string>> = {
  cuenta: "Conectá el número de WhatsApp del comercio",
};
/** Lo que hace el equipo, dicho para la persona de un cliente: no es una orden para ella. */
const TITULO_PASO_CLIENTE: Partial<Record<PasoPuestaEnMarcha["clave"], string>> = {
  cuenta: "Conectar tu número de WhatsApp",
  webhooks: "Activar la recepción de respuestas y bajas",
  optout: "Activar la baja con la palabra BAJA",
};

export default function ResumenOficialPage() {
  const { cuenta } = useCuentaOficial();
  // Una persona de un cliente ve el resumen de su comercio; crear y poner en marcha son del equipo.
  const equipo = useEsEquipoDeenex();
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [sinAtribuir, setSinAtribuir] = useState<RespuestaEntrante[]>([]);

  const pedir = useCallback(async (): Promise<string> => {
    try {
      const l = leerRespuesta<Resumen>(await oficialApi.resumen());
      if (!l.ok) return textoError(l);
      if (!l.data) return "El servidor no devolvió el resumen.";
      setResumen(l.data);
      return "";
    } catch (e) {
      return textoError(leerError(e));
    }
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(await pedir());
    setCargando(false);
  }, [pedir]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Respuestas sin campaña: una vez al entrar, sin polling. Si fallan, la sección no aparece
  // (no es un número que alguien vaya a leer como «no hubo ninguna»: sólo se muestran si hay).
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<PaginaRespuestas>(await oficialApi.respuestas({ sinAtribuir: true, limit: 10 }));
        if (vivo && l.ok && Array.isArray(l.data?.items)) setSinAtribuir(l.data.items);
      } catch {
        // la sección es opcional: sin datos, no se muestra
      }
    })();
    return () => { vivo = false; };
  }, []);

  const polling = usePolling(async () => {
    const e = await pedir();
    if (e) throw new Error(e);
    setError("");
  }, resumen ? REFRESCO_MS : null, []);

  if (cargando && !resumen) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" aria-busy="true" aria-label="Cargando el resumen">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-36 rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (!resumen) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <p className="text-sm font-semibold text-red-700">No pudimos traer el resumen.</p>
        <p className="text-sm text-red-700/80 mt-1">{error}</p>
        <button type="button" onClick={() => void cargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-800 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </div>
    );
  }

  const porEstado = resumen.plantillas?.porEstado || {};
  const aprobadas = Number(porEstado.approved) || 0;
  const pasos = pasosPuestaEnMarcha({
    modo: resumen.modoTransporte, cuenta, saldoUSD: resumen.saldo?.saldoUSD ?? 0, plantillasAprobadas: aprobadas, hayCampanaConfirmada: resumen.hayCampanaConfirmada,
  });
  const faltanPasos = pasos.some((p) => !p.hecho);
  // A una persona de un cliente, sin las alertas del equipo (la cuenta de Twilio de Deenex, la conexión: `alertasParaElComercio`).
  const deQuienMira = equipo ? resumen.alertas || [] : alertasParaElComercio(resumen.alertas || []);
  const alertas = alertasSinRepetir(deQuienMira, pasos, equipo).sort((a, b) => (ORDEN_NIVEL[a.nivel] ?? 3) - (ORDEN_NIVEL[b.nivel] ?? 3));
  const cupo = tileCupo(resumen);
  const numero = tileNumero(resumen);
  const noUsables = textoNoUsables(resumen.plantillas?.noUsables);
  const cupoPlantillas = resumen.plantillas?.cupo ? `${num(resumen.plantillas.cupo.usadas)} de ${num(resumen.plantillas.cupo.limite)} plantillas que permite Meta` : "";
  const saldo = resumen.saldo;
  const tope = saldo?.topeMensualUSD ?? null;

  return (
    <div className="space-y-6">
      {/* El título de la pantalla: desde que el encabezado de la sección es una fila chica, cada página dice cuál es.
          Crear plantillas y campañas es del equipo: a una persona de un cliente no se le ofrece. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* para el equipo, «Resumen»: su Inicio es el de Activa (como en el menú, `SeccionesOficial`) */}
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">{equipo ? "Resumen" : "Inicio"}</h2>
        {equipo && (
          <div className="flex items-center gap-2 flex-wrap">
          <Link to={`${R}/plantillas/nueva`} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <FileText size={15} /> Nueva plantilla
          </Link>
          <Link to={`${R}/campanas/nueva`} className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700">
            <Send size={15} /> Nueva campaña por el oficial
          </Link>
          </div>
        )}
      </div>

      {(polling.detenido || (error && resumen)) && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={15} />
          {polling.detenido ? "Dejamos de actualizar porque el servidor no responde." : `No pudimos actualizar: ${error}`}
          <button type="button" onClick={() => (polling.detenido ? polling.reintentar() : void cargar())} className="ml-auto font-semibold hover:underline">Reintentar</button>
        </div>
      )}

      {/* «Para empezar a enviar»: al equipo, cada paso con su acción («Conectá el número…» e «Ir»); el rótulo «lo hace el
          equipo de Deenex» se le mostraba al propio equipo. A una persona de un cliente, sólo lo que falta y hace el
          equipo, dicho así (no puede hacerlo ni ir a hacerlo). */}
      {equipo && faltanPasos && (
        <Tarjeta titulo="Para empezar a enviar">
          <ol className="space-y-2">
            {pasos.map((p) => (
              <li key={p.clave} className="flex items-center gap-3">
                {p.hecho
                  ? <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" aria-label="Hecho" />
                  : <Circle size={18} className="text-slate-300 flex-shrink-0" aria-label="Pendiente" />}
                <span className={`min-w-0 text-sm ${p.hecho ? "text-slate-400 line-through" : "text-slate-800"}`}>{TITULO_PASO_EQUIPO[p.clave] ?? p.titulo}</span>
                {!p.hecho && <Link to={p.ruta} className="ml-auto text-sm font-semibold text-violet-700 hover:underline">Ir</Link>}
              </li>
            ))}
          </ol>
        </Tarjeta>
      )}
      {!equipo && pasos.some((p) => !p.hecho && p.deDeenex) && (
        <Tarjeta titulo="Puesta en marcha">
          <ul className="space-y-2">
            {pasos.filter((p) => !p.hecho && p.deDeenex).map((p) => (
              <li key={p.clave} className="flex items-center gap-3">
                <Circle size={18} className="text-slate-300 flex-shrink-0" aria-label="Pendiente" />
                {/* el rótulo baja abajo del paso cuando no entra al lado (en el celular) */}
                <span className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm text-slate-800">{TITULO_PASO_CLIENTE[p.clave] ?? p.titulo}</span>
                  <span className="text-[11px] font-medium whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">lo hace el equipo de Deenex</span>
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {alertas.length > 0 && (
        <section aria-label="Alertas" className="space-y-2">
          {alertas.map((a, i) => <FilaAlerta key={`${a.codigo}-${i}`} a={a} equipo={equipo} />)}
        </section>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={<Smartphone size={15} />} label="Número de WhatsApp" value={numero.valor} vacio={numero.vacio} sub={numero.sub} />
        <StatTile icon={<Gauge size={15} />} label="Disponibles hoy" value={cupo.valor} vacio={cupo.vacio} sub={cupo.sub} meterPct={cupo.meterPct ?? undefined} tono="lectura" />
        <StatTile
          icon={<Wallet size={15} />}
          label="Saldo"
          value={usd(saldo?.saldoUSD)}
          sub={`Gastado este mes: ${usd(saldo?.gastoMesUSD)}${tope !== null ? ` de ${usd(tope)}` : ""}`}
          meterPct={tope ? Math.min(100, pct(saldo?.gastoMesUSD ?? 0, tope)) : undefined}
          tono="resultado"
          chip={saldo?.simulado ? { texto: "simulado", cls: "bg-amber-50 text-amber-700" } : undefined}
        />
        <StatTile
          icon={<FileText size={15} />}
          label="Plantillas aprobadas"
          value={num(aprobadas)}
          sub={[noUsables, cupoPlantillas].filter(Boolean).join(" · ") || undefined}
          tono="respuesta"
        />
      </div>

      {resumen.conversaciones && <TarjetaConversaciones c={resumen.conversaciones} />}

      <Tarjeta titulo="Plantillas por estado" right={<Link to={`${R}/plantillas`} className="text-sm font-semibold text-violet-700 hover:underline">Ver todas</Link>}>
        <div className="flex flex-wrap gap-2">
          {CHIPS_PLANTILLAS.map((c) => {
            const n = c.estados.reduce((s, e) => s + (Number(porEstado[e]) || 0), 0);
            return (
              <Link key={c.filtro} to={`${R}/plantillas?estado=${c.filtro}`}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium hover:opacity-80 ${c.cls}`}>
                {c.label} <span className="font-bold tabular-nums">{num(n)}</span>
              </Link>
            );
          })}
        </div>
      </Tarjeta>

      <Tarjeta titulo="Últimas campañas" right={<Link to={`${R}/campanas`} className="text-sm font-semibold text-violet-700 hover:underline">Ver todas</Link>}>
        {!resumen.ultimasCampanas?.length ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center">
            <p className="text-sm text-slate-500">Todavía no hay campañas.</p>
            {equipo && (
              <Link to={`${R}/campanas/nueva`} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700">
                <Plus size={15} /> Crear campaña
              </Link>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {resumen.ultimasCampanas.map((c) => {
              const avance = c.total ? Math.min(100, pct(c.enviados, c.total)) : 0;
              return (
                <li key={c.id}>
                  <Link to={`${R}/campanas/${encodeURIComponent(c.id)}`} className="block py-3 hover:bg-slate-50/60 -mx-2 px-2 rounded-lg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 truncate">{c.nombre}</span>
                      <ChipEstadoCampanaOficial estado={c.estado} />
                      {c.simulado === true && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">simulada</span>}
                      <span className="ml-auto text-xs text-slate-500 tabular-nums">{num(c.enviados)} de {num(c.total)} {plural(c.total, "enviado", "enviados")}</span>
                    </div>
                    <div className="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-violet-600 rounded-full" style={{ width: `${avance}%` }} />
                    </div>
                    {c.espera && ESPERA[c.espera] && <p className="text-xs text-amber-700 mt-1.5">{ESPERA[c.espera].titulo}</p>}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Tarjeta>

      {sinAtribuir.length > 0 && (
        <Tarjeta titulo="Respuestas que no pudimos atribuir a una campaña"
          sub="Llegaron al número pero no responden a un envío de los últimos 7 días. Se guardan igual.">
          <ListaRespuestas items={sinAtribuir} />
        </Tarjeta>
      )}
    </div>
  );
}

/**
 * A una persona de un cliente, lo que arregla el equipo (la conexión, cargar saldo, un borrador) no le ofrece el botón.
 * En el celular el botón va abajo del texto: al lado, sin cortarse, uno largo («Ver saldo del proveedor») dejaba el
 * mensaje en una columna de dos palabras.
 */
function FilaAlerta({ a, equipo }: { a: AlertaResumen; equipo: boolean }) {
  const d = equipo ? destinoAlerta(a) : destinoAlertaParaCliente(a);
  const estilo = a.nivel === "corte"
    ? { cls: "border-red-200 bg-red-50 text-red-800", icono: <OctagonAlert size={16} className="text-red-600 flex-shrink-0 mt-0.5" /> }
    : a.nivel === "alerta"
      ? { cls: "border-amber-200 bg-amber-50 text-amber-900", icono: <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" /> }
      : { cls: "border-slate-200 bg-white text-slate-700", icono: <Info size={16} className="text-slate-400 flex-shrink-0 mt-0.5" /> };
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${estilo.cls}`}>
      {estilo.icono}
      <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:gap-3">
        <p className="text-sm sm:flex-1">{a.mensaje}</p>
        {d ? (
          <Link to={d.ruta} className="text-sm font-semibold whitespace-nowrap hover:underline">{d.label}</Link>
        ) : (
          <span className="text-xs whitespace-nowrap opacity-80">Lo revisa el equipo de Deenex</span>
        )}
      </div>
    </div>
  );
}

/** «Por contestar» (con la ventana abierta) es parte de «sin responder»: van en una sola frase. */
function TarjetaConversaciones({ c }: { c: ResumenConversaciones }) {
  const porContestar = Number(c.porContestar) || 0;
  const sinResponder = Number(c.sinResponder) || 0;
  const texto = porContestar > 0
    ? `${num(porContestar)} por contestar (se les puede escribir hasta 24 h después) · ${num(sinResponder)} sin responder en total`
    : `Nadie está esperando respuesta.${sinResponder > 0 ? ` · ${num(sinResponder)} sin responder en total` : ""}`;
  return (
    <Tarjeta titulo="Conversaciones" right={(
      <Link to={`${R_CONVERSACIONES}?filtro=por_contestar`} className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline">
        <MessagesSquare size={14} /> Ir a Conversaciones
      </Link>
    )}>
      <p className={`text-sm ${porContestar > 0 ? "text-slate-800" : "text-slate-500"}`}>{texto}</p>
    </Tarjeta>
  );
}
